import type { CgtFieldType, FieldDefinition, TableCellConfig, TableCellType } from '@/types/cgt'

/**
 * A table cell that IS a Field Library field.
 *
 * Placing a library field on a form copies it and stamps `libraryFieldId`; a
 * table cell gets the same treatment, chosen per cell in the builder's Cell
 * types grid. Linking a cell does two things, both mirroring a placed field:
 *
 *  - the cell takes the library field's type (and its options, for a dropdown),
 *    so the author does not re-declare what the library already says;
 *  - `libraryFieldId` is stamped, which is all anything downstream reads. Split
 *    and Pool, for one, find their inputs through the linked field's system
 *    name — the cell does not need to know it is a "lab value".
 *
 * Pure, so the rules are testable without a DOM (vitest runs `.ts` only).
 */

/** A Field Library field offered for a cell, already reduced to what a cell can be. */
export interface CellLibraryField {
  id: string
  label: string
  systemName?: string
  cellType: TableCellType
  options?: string[]
}

/**
 * The cell type a library field becomes, or null when no cell can hold it.
 *
 * A textarea is text in a cell — a grid has no room for a multi-line box. The
 * rest have no cell equivalent: a calculated cell's formula is per cell and
 * cannot be copied from a field's, and signatures, processes, tables, sections,
 * images, attachments, descriptions and reviews are not values a cell holds.
 */
export function cellTypeOfLibraryField(fieldType: CgtFieldType): TableCellType | null {
  switch (fieldType) {
    case 'text':
    case 'textarea':
      return 'text'
    case 'number':
    case 'date':
    case 'datetime':
    case 'dropdown':
    case 'checkbox':
      return fieldType
    default:
      return null
  }
}

/** Every library field a cell can be linked to, sorted by label. */
export function toCellLibraryFields(fields: readonly FieldDefinition[]): CellLibraryField[] {
  const out: CellLibraryField[] = []
  for (const f of fields) {
    const cellType = cellTypeOfLibraryField(f.fieldType)
    if (!cellType) continue
    out.push({
      id: f.id,
      label: f.label,
      ...(f.systemName ? { systemName: f.systemName } : {}),
      cellType,
      ...(cellType === 'dropdown' && f.options?.length ? { options: [...f.options] } : {}),
    })
  }
  return out.sort((a, b) => a.label.localeCompare(b.label))
}

/**
 * The cell after linking it to `field`, or unlinking it when `field` is undefined.
 *
 * Linking replaces the cell's type with the field's and drops whatever config
 * belonged to the old type (a formula, a description's text). Unlinking keeps
 * the type the cell had: the author picked a field, not a type, and losing the
 * type on unlink would silently change what the cell accepts.
 *
 * Settings that are about the cell's PLACE rather than its value — the LIS
 * stage override and anchoring — are untouched either way.
 */
export function linkCell(prev: TableCellConfig, field: CellLibraryField | undefined): TableCellConfig {
  if (!field) {
    const next = { ...prev }
    delete next.libraryFieldId
    delete next.librarySystemName
    return next
  }
  const next: TableCellConfig = { ...prev, type: field.cellType, libraryFieldId: field.id }
  delete next.librarySystemName
  delete next.formula
  delete next.displayFormat
  delete next.constantText
  if (field.cellType === 'dropdown' && field.options?.length) next.options = [...field.options]
  else delete next.options
  return next
}

/**
 * Whether a cell keeps its library link when its type is changed by hand.
 *
 * Only if the new type is still the linked field's: a cell holding a library
 * number that becomes a date is no longer that field. A link to a field this
 * list does not know (another tenant's, or since removed) cannot be checked,
 * so it is kept rather than dropped on a guess.
 */
export function keepsLinkOnTypeChange(
  libraryFieldId: string | undefined,
  newType: TableCellType | undefined,
  fields: readonly CellLibraryField[],
): boolean {
  if (!libraryFieldId) return false
  const field = fields.find(f => f.id === libraryFieldId)
  return !field || field.cellType === newType
}

/**
 * The library ids already linked to a cell of this table, other than `exceptKey`.
 *
 * Offered disabled in the picker: one field placed twice on a form is two
 * places claiming to be the same value, which Split and Pool refuse as
 * ambiguous — so the builder steers away from it at the source.
 */
export function linkedLibraryIds(
  cells: Record<string, { libraryFieldId?: string }> | undefined,
  exceptKey: string,
): Set<string> {
  const ids = new Set<string>()
  for (const [key, cell] of Object.entries(cells ?? {})) {
    if (key !== exceptKey && cell.libraryFieldId) ids.add(cell.libraryFieldId)
  }
  return ids
}
